import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { SoftwarePackage } from "../types";

/**
 * Coverage — "which machines do NOT have this?"
 *
 * The Library tab answers "what can I deploy" and Deployments answers "what
 * did I deploy". Neither answers the question anyone actually asks before a
 * rollout, which is who is missing it — and answering that by eye across a few
 * hundred devices is how machines get missed.
 *
 * The three-way split comes from the server and is the point of the screen:
 * INSTALLED, MISSING, and NOT INVENTORIED — the last being machines that have
 * never reported an inventory, so we genuinely cannot say. Only MISSING is
 * offered for deployment; pushing an installer at a machine we have never
 * inventoried is a guess, and it is the guess that reinstalls over a working
 * copy. The reasoning lives in services/software_gap.py.
 */

type App = { name: string; device_count: number };
type Row = {
  device_id: number;
  hostname: string;
  status: string | null;
  version: string | null;
};
type Coverage = {
  name: string;
  matched_names: string[];
  installed: Row[];
  missing: Row[];
  unknown: Row[];
  counts: { installed: number; missing: number; unknown: number; total: number };
  packages: { id: number; name: string; kind: string }[];
};

type Bucket = "missing" | "installed" | "unknown";

// Mirrors the server's default `limit` on /coverage/applications.
const APP_LIST_CAP = 400;

export default function SoftwareCoverage({
  canManage,
  packages,
  onDeploy,
}: {
  canManage: boolean;
  packages: SoftwarePackage[];
  onDeploy: (pkg: SoftwarePackage) => void;
}) {
  const { currentCustomerId } = useCustomer();
  const [apps, setApps] = useState<App[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("");
  const [result, setResult] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState<Bucket>("missing");

  useEffect(() => {
    api
      .get("/api/v1/software/coverage/applications", {
        params: currentCustomerId ? { customer_id: currentCustomerId } : {},
      })
      .then((r) => setApps(r.data || []))
      .catch(() => setApps([]));
  }, [currentCustomerId]);

  useEffect(() => {
    if (!selected) {
      setResult(null);
      return;
    }
    setLoading(true);
    api
      .get("/api/v1/software/coverage", {
        params: {
          name: selected,
          ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
        },
      })
      .then((r) => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [selected, currentCustomerId]);

  const trimmed = query.trim();
  const matching = useMemo(() => {
    const q = trimmed.toLowerCase();
    return q ? apps.filter((a) => a.name.toLowerCase().includes(q)) : apps;
  }, [apps, trimmed]);
  const shortlist = useMemo(() => matching.slice(0, 60), [matching]);
  const matchingCount = matching.length;
  // Typing the exact name of a single entry needs no extra row.
  const exactMatch = matching.some((a) => a.name.toLowerCase() === trimmed.toLowerCase());

  const rows: Row[] = result ? result[bucket] : [];
  const counts = result?.counts;

  const BUCKETS: [Bucket, string, string][] = [
    ["missing", "Missing", "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30"],
    ["installed", "Installed", "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30"],
    ["unknown", "Not inventoried", "text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700"],
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 self-start">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find an application…"
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mb-2"
        />
        <div className="max-h-[28rem] overflow-y-auto -mx-1">
          {/* Ask about the typed term itself, not just an exact inventory
              string. Vendors version their display names, so the list under
              "7-Zip" is four builds and none of them is the question the
              person meant: clicking "7-Zip 22.01 (x64)" asks who lacks THAT
              BUILD, which conflates "missing the app" with "on an older
              version". Both are useful; they are not the same, and only one of
              them was reachable. */}
          {trimmed && !exactMatch && (
            <button
              onClick={() => setSelected(trimmed)}
              className={`w-full text-left px-2 py-1.5 rounded-lg text-xs mb-1 border border-dashed ${
                selected === trimmed
                  ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
                  : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }`}
            >
              Anything matching “<span className="font-medium">{trimmed}</span>”
              {/* Deliberately no count here. This list is the 400
                  most-installed names; the search behind it is over ALL of
                  inventory, so any total taken from the list would understate
                  it. The result panel reports what actually matched. */}
              <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                searches every inventory name, not just the list below
              </span>
            </button>
          )}
          {shortlist.length === 0 && !trimmed && (
            <p className="text-xs text-gray-400 dark:text-gray-500 px-2 py-4 text-center">
              Nothing matches.
            </p>
          )}
          {shortlist.map((a) => (
            <button
              key={a.name}
              onClick={() => setSelected(a.name)}
              className={`w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center justify-between gap-2 ${
                selected === a.name
                  ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-300"
              }`}
            >
              <span className="truncate" title={a.name}>
                {a.name}
              </span>
              <span className="shrink-0 text-gray-400 dark:text-gray-500">{a.device_count}</span>
            </button>
          ))}
        </div>
        {matchingCount > shortlist.length && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 px-1">
            Showing {shortlist.length} of {matchingCount} — type to narrow.
          </p>
        )}
        {apps.length >= APP_LIST_CAP && (
          // Never let a cap read as "that is everything".
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 px-1">
            List shows the {APP_LIST_CAP} most-installed applications. Searching by name still
            covers the rest.
          </p>
        )}
      </div>

      <div>
        {!selected ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pick an application to see who has it and who does not.
            </p>
          </div>
        ) : loading || !result ? (
          <p className="text-sm text-gray-400">Working…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {BUCKETS.map(([key, label, style]) => (
                <button
                  key={key}
                  onClick={() => setBucket(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${style} ${
                    bucket === key
                      ? "border-current"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  {label} · {counts ? counts[key] : 0}
                </button>
              ))}

              {canManage && bucket === "missing" && result.packages.length > 0 && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Deploy:</span>
                  {result.packages.map((p) => {
                    const full = packages.find((x) => x.id === p.id);
                    return (
                      <button
                        key={p.id}
                        disabled={!full}
                        onClick={() => full && onDeploy(full)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40"
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Say what the substring actually caught, so the counts can be
                checked rather than taken on faith. "7-Zip" legitimately
                matches four differently-versioned inventory strings. */}
            {result.matched_names.length > 0 && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                Matched {result.matched_names.length} inventory{" "}
                {result.matched_names.length === 1 ? "name" : "names"}:{" "}
                <span className="font-mono">{result.matched_names.slice(0, 6).join(", ")}</span>
                {result.matched_names.length > 6 ? " …" : ""}
              </p>
            )}

            {bucket === "unknown" && (
              <p className="text-xs text-gray-600 dark:text-gray-300 bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 mb-3">
                These machines have never reported a software inventory, so we cannot say whether
                they have it. They are deliberately kept out of the missing list — deploying to a
                machine we have not inventoried is a guess.
              </p>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Device
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    {bucket === "installed" && (
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Version
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        {bucket === "missing" ? "Nothing is missing it — full coverage." : "None."}
                      </td>
                    </tr>
                  )}
                  {rows.map((d) => (
                    <tr key={d.device_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2">
                        <Link
                          to={`/devices/${d.device_id}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {d.hostname}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            d.status === "online"
                              ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          }`}
                        >
                          {d.status || "unknown"}
                        </span>
                      </td>
                      {bucket === "installed" && (
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-300 font-mono text-xs">
                          {d.version || "—"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
